// §34.2 - shop hours settings: per-weekday closed toggle + open/close time
// selects backing workspaces.operating_hours (migration 0041), which the
// existing booking flow already reads via loadWorkspaceBookingConfig.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DEFAULT_OPERATING_HOURS, WEEKDAYS, type OperatingHours, type Weekday,
} from '@wrapforce/shared';
import { apiErrorMessage } from '@/lib/api-errors';
import { useLocaleFormatters } from '@/lib/format';
import { useUpdateWorkspace, useWorkspaceDetail } from '@/features/workspace/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/input';

// 2024-01-01 is a Monday - used only as a reference date so weekday names can
// be formatted locale-aware via date-fns, without needing per-day i18n keys.
const REFERENCE_MONDAY = new Date(Date.UTC(2024, 0, 1));

const OPEN_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30); // 00:00..23:30
const CLOSE_OPTIONS = Array.from({ length: 48 }, (_, i) => (i + 1) * 30); // 00:30..24:00

export function ShopHoursSettings({ workspaceId }: { workspaceId: string | null }) {
  const { data: detail } = useWorkspaceDetail(workspaceId);
  if (!detail) return null;
  return (
    <ShopHoursForm
      key={JSON.stringify(detail.operatingHours)}
      workspaceId={workspaceId}
      initial={detail.operatingHours ?? DEFAULT_OPERATING_HOURS}
    />
  );
}

function ShopHoursForm({ workspaceId, initial }: { workspaceId: string | null; initial: OperatingHours }) {
  const { t } = useTranslation('settings');
  const { formatDate, formatTimeOfDay } = useLocaleFormatters();
  const updateWorkspace = useUpdateWorkspace(workspaceId);
  const [hours, setHours] = useState<OperatingHours>(initial);

  function dayName(index: number) {
    const date = new Date(REFERENCE_MONDAY);
    date.setUTCDate(date.getUTCDate() + index);
    return formatDate(date, 'EEEE');
  }

  function setDay(day: Weekday, value: OperatingHours[Weekday]) {
    setHours((prev) => ({ ...prev, [day]: value }));
  }

  function toggleClosed(day: Weekday, closed: boolean) {
    setDay(day, closed ? null : { open: 480, close: 1080 });
  }

  const errors: Partial<Record<Weekday, string>> = {};
  for (const day of WEEKDAYS) {
    const value = hours[day];
    if (value && value.open >= value.close) errors[day] = t('shopHours.invalidRange');
  }
  const hasErrors = Object.keys(errors).length > 0;

  async function handleSave() {
    try {
      await updateWorkspace.mutateAsync({ operatingHours: hours });
      toast.success(t('shopHours.saved'));
    } catch (err) {
      toast.error(t('shopHours.saveError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shopHours.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {WEEKDAYS.map((day, index) => {
            const value = hours[day];
            const closed = value === null;
            return (
              <div key={day} className="flex flex-col gap-1.5 border-b border-border pb-3 last:border-b-0 last:pb-0">
                <div className="flex items-center gap-4">
                  <span className="w-24 flex-none text-sm font-semibold text-ink">{dayName(index)}</span>
                  <label className="flex flex-none cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={closed}
                      onChange={(e) => toggleClosed(day, e.target.checked)}
                      className="h-4 w-4 accent-[var(--color-accent)]"
                    />
                    <span className="text-sm text-muted">{t('shopHours.closed')}</span>
                  </label>
                  {!closed && value ? (
                    <div className="flex flex-1 items-center gap-2">
                      <select
                        value={value.open}
                        onChange={(e) => setDay(day, { ...value, open: Number(e.target.value) })}
                        className="h-9 border border-border bg-surface px-2 text-sm"
                      >
                        {OPEN_OPTIONS.map((m) => (
                          <option key={m} value={m}>{formatTimeOfDay(m)}</option>
                        ))}
                      </select>
                      <span className="text-sm text-muted">{t('shopHours.to')}</span>
                      <select
                        value={value.close}
                        onChange={(e) => setDay(day, { ...value, close: Number(e.target.value) })}
                        className="h-9 border border-border bg-surface px-2 text-sm"
                      >
                        {CLOSE_OPTIONS.map((m) => (
                          <option key={m} value={m}>{formatTimeOfDay(m)}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
                <FieldError>{errors[day]}</FieldError>
              </div>
            );
          })}
        </div>
        <Button onClick={handleSave} loading={updateWorkspace.isPending} disabled={hasErrors} className="w-fit">
          {t('common:actions.save')}
        </Button>
      </CardContent>
    </Card>
  );
}
